import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete, Libraries } from '@react-google-maps/api';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification, extractAddressComponents } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { IAddStoreForm, IStoreResponse, IStore } from '@/interfaces/Store/store.interface';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { Clock, MapPin, User, UtensilsCrossed, Image as ImageIcon } from 'lucide-react';
import MultiImageUploader from '@/components/common/MultiImageUploader';
import { CommonSelect } from '@/components/common/CommonSelect';
import { COUNTRY_CODES } from '@/utility/Country';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useActiveRole } from '@/hooks/useActiveRole';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY || '';

const defaultLocation = {
    lat: 28.022936,
    lng: 73.311913
};

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '12px'
};

const LIBRARIES: Libraries = ['places'];

export const AddStore = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();

    const [loadingData, setLoadingData] = useState(isEditMode);
    const [markerPosition, setMarkerPosition] = useState(defaultLocation);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        control,
        formState: { errors, isSubmitting },
    } = useForm<IAddStoreForm>({
        defaultValues: {
            countryCode: '+27', // Default
            takeawayOrderStatus: 'inactive',
            deliveryOrderStatus: 'inactive',
            courierOrderStatus: 'inactive',
            commission: '0',
        }
    });

    const [searchBox, setSearchBox] = useState<google.maps.places.Autocomplete | null>(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY,
        libraries: LIBRARIES
    });

    const currentOpeningTime = watch('openingTime');
    const currentClosingTime = watch('closingTime');

    console.log("errors", markerPosition);

    useEffect(() => {
        const fetchStore = async () => {
            if (!id) return;
            try {
                const response = await callApi<{ data: IStore }>(
                    `${API_URL}${API_ENDPOINT.stores}/detail/${id}`,
                    { method: 'GET' }
                );

                const storeData = response?.data;
                const lat = storeData.latitude ? parseFloat(storeData.latitude) : defaultLocation.lat;
                const lng = storeData.longitude ? parseFloat(storeData.longitude) : defaultLocation.lng;

                setMarkerPosition({ lat, lng });

                reset({
                    name: storeData.name || '',
                    description: storeData.description || '',
                    address: storeData.address || '',
                    contact: storeData.contact || '',
                    countryCode: storeData.countryCode || '+27',
                    openingTime: storeData.settings?.openingTime || '',
                    closingTime: storeData.settings?.closingTime || '',
                    latitude: lat.toString(),
                    longitude: lng.toString(),
                    // postcode: storeData.postcode || '',
                    // suburb: storeData.suburb || '',
                    // area: storeData.area || '',
                    // locality: storeData.locality || '',
                    // city: storeData.city || '',
                    // state: storeData.state || '',
                    // country: storeData.country || '',
                    takeawayOrderStatus: storeData.settings?.takeawayOrderStatus || '',
                    deliveryOrderStatus: storeData.settings?.deliveryOrderStatus || '',
                    courierOrderStatus: storeData.settings?.courierOrderStatus || '',
                    commission: storeData.settings?.commission || '',
                    storeLogo: storeData.media?.filter(m => m.referenceType === 'store_logo').map(m => `${API_URL}/${m.url}`) || [],
                    storeBanner: storeData.media?.filter(m => m.referenceType === 'store_banner').map(m => `${API_URL}/${m.url}`) || [],
                } as IAddStoreForm);

            } catch (error) {
                ThrowError(error);
                navigate(-1);
            } finally {
                setLoadingData(false);
            }
        };

        if (isEditMode) {
            fetchStore();
        } else {
            setValue('latitude', defaultLocation.lat.toString());
            setValue('longitude', defaultLocation.lng.toString());
        }
    }, [id, isEditMode, reset, setValue, navigate]);

    const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();
            setMarkerPosition({ lat: newLat, lng: newLng });
            setValue('latitude', newLat.toString(), { shouldValidate: true });
            setValue('longitude', newLng.toString(), { shouldValidate: true });

            // Reverse geocoding to autofill address and components
            // const geocoder = new google.maps.Geocoder();
            // geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
            //     if (status === 'OK' && results && results[0]) {
            //         const place = results[0];
            //         setValue('address', place.formatted_address, { shouldValidate: true });

            //         const comps = extractAddressComponents(place.address_components);
            //         setValue('postcode', comps.postcode);
            //         setValue('suburb', comps.suburb);
            //         setValue('area', comps.area);
            //         setValue('locality', comps.locality);
            //         setValue('city', comps.city);
            //         setValue('state', comps.state);
            //         setValue('country', comps.country);
            //     }
            // });
        }
    }, [setValue]);

    const onLoadAutocomplete = (autocomplete: google.maps.places.Autocomplete) => {
        setSearchBox(autocomplete);
    };

    const onPlaceChanged = () => {
        if (searchBox !== null) {
            const place = searchBox.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setMarkerPosition({ lat: newLat, lng: newLng });
                setValue('latitude', newLat.toString(), { shouldValidate: true });
                setValue('longitude', newLng.toString(), { shouldValidate: true });

                // Optionally update address input if formatted_address exists
                if (place.formatted_address) {
                    setValue('address', place.formatted_address, { shouldValidate: true });
                }

                // if (place.address_components) {
                //     const comps = extractAddressComponents(place.address_components);
                //     setValue('postcode', comps.postcode);
                //     setValue('suburb', comps.suburb);
                //     setValue('area', comps.area);
                //     setValue('locality', comps.locality);
                //     setValue('city', comps.city);
                //     setValue('state', comps.state);
                //     setValue('country', comps.country);
                // }
            }
        }
    };

    const onSubmit = async (data: IAddStoreForm) => {
        try {
            const url = isEditMode
                ? `${API_URL}${API_ENDPOINT.updateStore}`
                : `${API_URL}${API_ENDPOINT.stores}/create`;

            const method = isEditMode ? 'PUT' : 'POST';

            const formData = new FormData();

            // Trim and append text fields
            if (isEditMode) {
                formData.append('storeId', id);
            }
            formData.append('name', data.name?.trim());
            formData.append('description', data.description?.trim() || '');
            formData.append('address', data.address?.trim());
            formData.append('contact', data.contact?.trim());
            formData.append('openingTime', data.openingTime);
            formData.append('closingTime', data.closingTime);
            formData.append('latitude', markerPosition.lat.toString());
            formData.append('longitude', markerPosition.lng.toString());
            formData.append('takeawayOrderStatus', data.takeawayOrderStatus);
            formData.append('deliveryOrderStatus', data.deliveryOrderStatus);
            formData.append('courierOrderStatus', data.courierOrderStatus);
            formData.append('commission', data.commission || '0');

            // // Append granular address components
            // formData.append('postcode', data.postcode || '');
            // formData.append('suburb', data.suburb || '');
            // formData.append('area', data.area || '');
            // formData.append('locality', data.locality || '');
            // formData.append('city', data.city || '');
            // formData.append('state', data.state || '');
            // formData.append('country', data.country || '');

            if (!isEditMode) {
                formData.append('fullName', data.fullName?.trim());
                formData.append('email', data.email?.trim());
                formData.append('password', data.password?.trim());
            }

            // Append Logo
            if (data.storeLogo && data.storeLogo.length > 0) {
                data.storeLogo.forEach((img) => {
                    if (typeof img !== 'string') {
                        formData.append('storeLogo', img);
                    }
                });
            }

            // Append Banner
            if (data.storeBanner && data.storeBanner.length > 0) {
                data.storeBanner.forEach((img) => {
                    if (typeof img !== 'string') {
                        formData.append('storeBanner', img);
                    }
                });
            }

            const response = await callApi<IStoreResponse>(
                url,
                {
                    method,
                    data: formData,
                    multipart: true
                },
            );

            showToastNotification('success', response?.message);
            navigate(-1);
        } catch (error) {
            console.log("Error:", error);
            ThrowError(error);
        }
    };

    if (loadingData) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className=" space-y-6 pb-20 animate-in fade-in duration-500">
                <MainHeading
                    title={isEditMode ? "Edit Store" : "Add New Store"}
                    showBack
                    onBack={() => navigate(-1)}
                />

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                    {/* ── Manager (Account) Info ── */}
                    {!isEditMode && (
                        <SectionCard title="Manager Account" icon={<User className="w-5 h-5 text-muted-foreground" />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    label="Full Name"
                                    name="fullName"
                                    type="text"
                                    requiredIcon
                                    placeholder="John Doe"
                                    error={errors.fullName?.message}
                                    {...register('fullName', {
                                        ...errorTypes.required('Full Name'),
                                    })}
                                />
                                <FormField
                                    label="Email"
                                    name="email"
                                    type="email"
                                    requiredIcon
                                    placeholder="manager@example.com"
                                    error={errors.email?.message}
                                    {...register('email', {
                                        ...errorTypes.required('Email'),
                                        ...errorTypes.email,
                                    })}
                                />
                                <FormField
                                    label="Password"
                                    name="password"
                                    type="password"
                                    requiredIcon
                                    placeholder="Min. 6 characters"
                                    error={errors.password?.message}
                                    {...register('password', {
                                        ...errorTypes.required('Password'),
                                        minLength: { value: 6, message: 'Password must be at least 6 characters' },
                                    })}
                                />
                            </div>
                        </SectionCard>
                    )}

                    {/* ── Store Info ── */}
                    <SectionCard title="Store Details" icon={<UtensilsCrossed className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                label="Store Name"
                                name="name"
                                type="text"
                                requiredIcon
                                placeholder="e.g. My Tasty Store"
                                error={errors.name?.message}
                                {...register('name', {
                                    ...errorTypes.required('Store Name'),
                                })}
                            />
                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <Controller
                                        name="countryCode"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex flex-col">
                                                <label className="block text-sm font-medium text-foreground mb-1.5">Code</label>
                                                <CommonSelect
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    options={COUNTRY_CODES}
                                                    error={errors.countryCode?.message}
                                                    triggerClassName="w-full h-10 rounded-md border-border shadow-none"
                                                    disabled={true}
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <FormField
                                        label="Contact Number"
                                        name="contact"
                                        type="text"
                                        requiredIcon
                                        placeholder="123456789"
                                        wrapperClass="m-0"
                                        error={errors.contact?.message}
                                        onInput={(e: React.FormEvent<HTMLInputElement>) => {
                                            const target = e.target as HTMLInputElement;
                                            target.value = target.value.replace(/[^0-9]/g, '');
                                        }}
                                        {...register('contact', {
                                            ...errorTypes.required('Contact'),
                                            ...errorTypes.phone,
                                        })}
                                    />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <FormField
                                    label="Description"
                                    name="description"
                                    type="textarea"
                                    rows={3}
                                    placeholder="Brief description of the store"
                                    error={errors.description?.message}
                                    {...register('description')}
                                />
                            </div>
                            <FormField
                                label="Commission (%)"
                                name="commission"
                                type="number"
                                requiredIcon
                                placeholder="e.g. 10"
                                error={errors.commission?.message}
                                onWheel={(e) => e.currentTarget.blur()}
                                {...register('commission', {
                                    ...errorTypes.required('Commission'),
                                    ...errorTypes.min('Commission', 0),
                                    ...errorTypes.max('Commission', 100)
                                })}
                            />
                        </div>
                    </SectionCard>

                    {/* ── Media Uploads ── */}
                    <SectionCard title="Store Branding" icon={<ImageIcon className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <Controller
                                name="storeLogo"
                                control={control}
                                render={({ field }) => (
                                    <MultiImageUploader
                                        label="Store Logo"
                                        value={field.value || []}
                                        onChange={field.onChange}
                                        maxFiles={1}
                                        error={errors.storeLogo?.message}
                                        type=".jpg,.jpeg,.png,.webp"
                                    />
                                )}
                            />
                            <Controller
                                name="storeBanner"
                                control={control}
                                render={({ field }) => (
                                    <MultiImageUploader
                                        label="Store Banner"
                                        value={field.value || []}
                                        onChange={field.onChange}
                                        maxFiles={1}
                                        error={errors.storeBanner?.message}
                                        type=".jpg,.jpeg,.png,.webp"
                                    />
                                )}
                            />
                        </div>
                    </SectionCard>

                    {/* ── Map Location Selector ── */}
                    <SectionCard
                        title="Spatial Data"
                        icon={<MapPin className="w-5 h-5 text-muted-foreground" />}
                    >
                        <p className="text-xs text-muted-foreground mb-4">
                            Type to search for a location, or drag and drop the map marker to precisely set the store's geographic coordinates.
                        </p>

                        {isLoaded && (
                            <div className="mb-4">
                                <Autocomplete
                                    onLoad={onLoadAutocomplete}
                                    onPlaceChanged={onPlaceChanged}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search for a location..."
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                </Autocomplete>
                            </div>
                        )}

                        <div className="overflow-hidden border border-secondary/20 rounded-xl bg-secondary/5 mb-4">
                            {isLoaded ? (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={markerPosition}
                                    zoom={15}
                                    options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false }}
                                >
                                    <Marker
                                        position={markerPosition}
                                        draggable={true}
                                        onDragEnd={onMarkerDragEnd}
                                        animation={google.maps.Animation.DROP}
                                    />
                                </GoogleMap>
                            ) : (
                                <div className="flex items-center justify-center w-full h-[400px] bg-secondary/10">
                                    <span className="text-sm text-muted-foreground animate-pulse">Loading Maps...</span>
                                </div>
                            )}
                        </div>

                        <FormField
                            label="Physical Address"
                            name="address"
                            type="textarea"
                            rows={3}
                            requiredIcon
                            placeholder="123 Main St, City"
                            error={errors.address?.message}
                            {...register('address', {
                                ...errorTypes.required('Address'),
                            })}
                        />
                    </SectionCard>

                    {/* ── Operating Hours ── */}
                    <SectionCard title="Operating Hours" icon={<Clock className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                label="Opening Time"
                                name="openingTime"
                                type="time"
                                requiredIcon
                                max={currentClosingTime || undefined}
                                error={errors.openingTime?.message}
                                {...register('openingTime', {
                                    ...errorTypes.required('Opening Time'),
                                    onChange: (e) => {
                                        const val = e.target.value;
                                        if (currentClosingTime && val > currentClosingTime) {
                                            setValue('openingTime', currentClosingTime, { shouldValidate: true });
                                        }
                                    }
                                })}
                            />

                            <FormField
                                label="Closing Time"
                                name="closingTime"
                                type="time"
                                requiredIcon
                                min={currentOpeningTime || undefined}
                                error={errors.closingTime?.message}
                                {...register('closingTime', {
                                    ...errorTypes.required('Closing Time'),
                                    onChange: (e) => {
                                        const val = e.target.value;
                                        if (currentOpeningTime && val < currentOpeningTime) {
                                            setValue('closingTime', currentOpeningTime, { shouldValidate: true });
                                        }
                                    }
                                })}
                            />
                        </div>
                    </SectionCard>

                    {/* ── Logistics & Order Settings ── */}
                    <SectionCard title="Logistics & Order Settings" icon={<UtensilsCrossed className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Status Toogles */}
                            <div className="space-y-6 md:col-span-2">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-medium">Collection Status</Label>
                                            <p className="text-xs text-muted-foreground">Enable collection orders</p>
                                        </div>
                                        <Controller
                                            name="takeawayOrderStatus"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value === 'active'}
                                                    onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-medium">Delivery Status</Label>
                                            <p className="text-xs text-muted-foreground">Enable home delivery</p>
                                        </div>
                                        <Controller
                                            name="deliveryOrderStatus"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value === 'active'}
                                                    onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-medium">Courier Status</Label>
                                            <p className="text-xs text-muted-foreground">Enable courier service</p>
                                        </div>
                                        <Controller
                                            name="courierOrderStatus"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value === 'active'}
                                                    onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Conditional Charge Fields removed as they are now global */}
                        </div>
                    </SectionCard>

                    {/* ── Actions ── */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-dashed">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" isLoading={isSubmitting} loadingText={isEditMode ? "Saving Changes..." : "Creating Store..."}>
                            {isEditMode ? "Save Store Changes" : "Create Store"}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};